import React from 'react'
import { useIntl } from 'react-intl'
import { useAccess } from 'contexts/access'
import { useEntitiesClientState, useEntities } from 'state/entities'
import { omit } from 'utilities/object'
import { compact } from 'utilities/array'
import {
    categories as competenceCategories,
    categoryToMetaMap as competenceCategoryToMetaMap
} from 'pages/competence/constants/category'
import { FilterRow } from './s'
import Filter from 'components/filter'

const EntityPickerFilter = ({ namespace, salt }) => {
    const { formatMessage } = useIntl()

    const { checkFeature } = useAccess()
    const companiesAvailable = checkFeature('company-groups')

    const {
        path,
        filter,
        filterOptions,
        getInitialFilter,
        setFilter
    } = useEntitiesClientState(state => ({
        path: state.path,
        filter: state.filter,
        filterOptions: state.filterOptions,
        getInitialFilter: state.getInitialFilter,
        setFilter: state.setFilter
    }))

    const {
        items,
        isFetching,
        isFetched
    } = useEntities()

    const {
        genericGroupFilterAppliedInitially,
        ...fields
    } = getFilterFields({
        path,
        initialFilter: getInitialFilter(),
        overrides: filterOptions?.overrides,
        companiesAvailable,
        formatMessage
    })

    if(!Object.values(fields).length) {
        return null
    }

    // The genericGroupFilterAppliedInitially stuff basically hides the types filter from the user
    // so that they don’t have to uncheck the default in order to pick other specific types

    return (
        <FilterRow>
            <Filter
                namespace={namespace}
                filter={(genericGroupFilterAppliedInitially && genericGroupFilterApplied(filter)) ?
                    omit(filter, 'types') :
                    filter
                }
                setFilter={filter => setFilter({
                    ...filter,
                    ...((genericGroupFilterAppliedInitially && !filter?.types?.length) ? { types: ['group'] } : null)
                })}
                {...fields}
                items={items}
                fetching={isFetching}
                hasFetched={isFetched}
                showerride={true}
                expandable={true}
                memoryFilterOptions={{ enabled: false }}
                className="compact"
                salt={salt} />
        </FilterRow>
    )
}

const searchable = [
    '/groups',
    '/units',
    '/job-titles',
    '/job-titles/paged',
    '/competence-types',
    '/equipment-types',
    '/documents/templates'
]

const getFilterFields = ({ path, initialFilter, overrides, companiesAvailable, formatMessage }) => {
    const hideNames = Object.keys(initialFilter ?? {})

    const search = {
        type: 'search',
        name: 'search',
        controlProps: {
            placeholder: formatMessage({
                id: 'search_placeholder',
                defaultMessage: 'Start typing to search…'
            }),
            ...(overrides?.search?.controlProps ?? null),
            ...(!!overrides?.search ? { autoFocus: true } : null)
        },
        ...omit(overrides?.search ?? {}, 'controlProps')
    }

    let basicFields = []
    let advancedFields = []

    // These endpoints support filters that are considered
    // too advanced and/or undesirable for a user to use
    if([
        '/roles', // domain, assignable, and deletable
        '/absence/types' // includeArchivedPolicies, includePolicies
    ].includes(path)) {
        return {}
    }

    if(searchable.includes(path) && !hideNames?.includes('search')) {
        basicFields.push(search)
    }

    const genericGroupFilterAppliedInitially = genericGroupFilterApplied(initialFilter)
    const groupsByGroupsEndpoint = path === '/groups'
    const groupsByUnitsEndpoint = path === '/units' && genericGroupFilterAppliedInitially

    if(groupsByGroupsEndpoint || groupsByUnitsEndpoint) {
        if(!hideNames?.includes('types') || genericGroupFilterAppliedInitially) {
            advancedFields.push({
                type: 'checkboxes',
                name: 'types',
                label: formatMessage({
                    id: 'noun_types',
                    defaultMessage: 'Types'
                }),
                field: {
                    options: compact([
                        companiesAvailable && {
                            id: 'company',
                            name: formatMessage({
                                id: 'noun_companies',
                                defaultMessage: 'Companies'
                            })
                        },
                        {
                            id: 'team',
                            name: formatMessage({
                                id: 'noun_teams',
                                defaultMessage: 'Teams'
                            })
                        },
                        {
                            id: 'location',
                            name: formatMessage({
                                id: 'noun_locations',
                                defaultMessage: 'Locations'
                            })
                        }
                    ])
                },
                ...(overrides?.types ?? null)
            })
        }
    }

    if(path === '/competence-types') {
        if(!hideNames?.includes('category')) {
            advancedFields.push({
                type: 'checkboxes',
                name: 'category',
                label: formatMessage({
                    id: 'noun_category',
                    defaultMessage: 'Category'
                }),
                field: {
                    options: competenceCategories.map(({ name }) => ({
                        id: name,
                        name: formatMessage(competenceCategoryToMetaMap[name].name)
                    }))
                },
                ...(overrides?.category ?? null)
            })
        }
    }

    if(!basicFields.length && !advancedFields.length) {
        return {}
    }

    return {
        basicFields,
        advancedFields,
        genericGroupFilterAppliedInitially
    }
}

const genericGroupFilterApplied = filter => filter?.types?.length === 1 && filter.types[0] === 'group'

export default EntityPickerFilter